import { styled } from "@mui/material";
import clsx from "clsx";
import { ReactNode, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";

interface IOwnProps {
    title?: string;
    titleComponent?: ReactNode;
    collapsible?: boolean;
    collapsed?: boolean;
    isLoading?: boolean;
    children: ReactNode;
    subTitle?: string;
    hasAccess?: boolean;
}

const CollapsibleSection = ({
    className,
    title,
    titleComponent,
    collapsible,
    collapsed,
    isLoading,
    children,
    subTitle,
    hasAccess = true,
}: IWithClassName & IOwnProps) => {
    const [isCollapsed, setIsCollapsed] = useState(collapsed ?? false);

    const toggleCollapse = () => {
        if (collapsible && !isLoading && hasAccess) {
            setIsCollapsed(!isCollapsed);
        }
    };

    return (
        <div className={className}>
            {title && (
                <div
                    className={clsx("collapsible-section-header", {
                        "collapsible-cursor": collapsible && hasAccess,
                        "collapsible-loading": isLoading,
                        "no-access": !hasAccess,
                    })}
                    onClick={toggleCollapse}
                >
                    {!hasAccess && (
                        <img
                            src="/assets/lock-icon.svg"
                            className="lock-icon"
                            alt="No Access"
                        />
                    )}
                    <p
                        className={clsx("collapsible-section-title", {
                            "grayed-out": !hasAccess,
                        })}
                    >
                        {title}
                    </p>
                    {collapsible && (
                        <img
                            className={clsx({
                                "grayed-out": !hasAccess,
                            })}
                            src={
                                isCollapsed
                                    ? "/assets/list-plus.svg"
                                    : "/assets/list-minus.svg"
                            }
                        />
                    )}
                    {!isCollapsed && titleComponent && (
                        <div className="collapsible-title-component">
                            {titleComponent}
                        </div>
                    )}
                </div>
            )}
            {!isCollapsed && subTitle && (
                <p className="collapsible-section-subtitle">{subTitle}</p>
            )}
            {!isCollapsed && (
                <div className="children-container">
                    {isLoading ? <Skeleton /> : children}
                </div>
            )}
        </div>
    );
};

export default styled(CollapsibleSection)(({ theme }: IWithThemeProps) => ({
    ".collapsible-section-header": {
        marginBottom: theme?.spacing(1.5),
        display: "flex",
    },
    ".collapsible-cursor:not(.collapsible-loading)": {
        cursor: "pointer",
    },
    ".no-access": {
        cursor: "default",
    },
    ".no-access .lock-icon": {
        cursor: "pointer",
    },
    ".collapsible-loading": {
        filter: "blur(4px)",
    },
    ".collapsible-section-title": {
        marginRight: theme?.spacing(2.5),
        color: theme?.palette.collapsibleFeatures.textColor,
    },
    ".children-container": {
        borderRadius: theme?.spacing(2.5),
        backgroundColor: theme?.palette.collapsibleFeatures.bgColor,
    },
    ".collapsible-title-component": {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
    },
    ".lock-icon": {
        marginRight: theme?.spacing(2),
    },
    ".grayed-out": {
        opacity: 0.3,
        cursor: "default",
    },
}));
